import { useFlowRouter } from '../useFlowRouter'
import { useMemo } from 'react'

function parseUrl(path: string): URL | undefined {
  try {
    // Convert to a url to extract the path and query params properly
    // The domain doesn't matter here since it's not used, but it must be something valid
    return new URL(path, 'http://something.com')
  } catch (error) {
    // Errors can happen if someone manually malforms the path so no need to
    // report on it, just don't use it
    return undefined
  }
}

function getInviteType(pathname: string): 'link' | 'email' | undefined {
  if (pathname === '/accept-invite') {
    return 'email'
  } else if (pathname.startsWith('/accept-org-invite/')) {
    return 'link'
  }
}

export interface InviteState {
  inviteType: 'link' | 'email' | 'direct'
  orgName?: string
  ssoDomain?: string
}

export function useInviteState(): InviteState | undefined {
  const flowRouter = useFlowRouter()
  const redirectToUrl = useMemo(() => {
    // redirectTo is double-encoded throughout the site so must decode it in addition
    // to the decoding that `router.query` already provides
    const value = decodeURIComponent(flowRouter.getString('redirectTo') || '') || undefined
    return value ? parseUrl(value) : undefined
  }, [flowRouter])
  return useMemo(() => {
    if (redirectToUrl) {
      const inviteType = getInviteType(redirectToUrl.pathname)
      if (!inviteType) return
      return {
        inviteType,
        orgName: redirectToUrl.searchParams.get('org') || undefined,
        ssoDomain: redirectToUrl.searchParams.get('ssoDomain') || undefined
      }
    } else {
      // SSO email invites bypass the usual /accept* routes and go just to /signin
      // but they should have org and ssoDomain params to indicate they came from
      // there and to indicate the need for the sso account to be used
      const inviteType = getInviteType(flowRouter.pathname)
      const orgName = flowRouter.getString('org')
      const ssoDomain = flowRouter.getString('ssoDomain')
      if (inviteType || ssoDomain) {
        return {
          inviteType: inviteType || 'direct',
          orgName,
          ssoDomain
        }
      }
    }
  }, [redirectToUrl, flowRouter])
}
